import { useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchUnits, type UnitsResponse } from "@/lib/api";
import { useDashboardOrg } from "@/app/DashboardOrgContext";
import { markRefreshSource, refreshSource, useFleetPollInterval } from "@/lib/liveRefresh";

export interface UseUnitsDataParams {
  search: string;
  state: string;
  sort: string;
  start: number;
  end: number;
  mode?: string;
  pricing?: string;
  protocol?: string;
}

export function useUnitsData(params: UseUnitsDataParams) {
  // Manager → org-scoped (/api/org/<id>/units); admin → fleet. orgId is in the query
  // key so switching org refetches; scopeReady gates so a manager never fires the
  // fleet endpoint before an org is selected.
  const { dashboardOrgId, scopeReady, isFleet, manageableOrgIds } = useDashboardOrg();

  // A non-admin who manages NO organization — a commercial "driver" OR a plain
  // residential user — has no org to select, so scopeReady never turns true and the
  // org path would fire nothing. But they DO have a personal unit list: the
  // /api/units compat bridge (0c80eed) scopes that endpoint to the caller's own
  // unit_users assignments. Route such a user there (orgId=null) and enable the
  // query so their assigned units — e.g. a residential charger owned by someone who
  // is also a driver in a commercial org — actually show. Managers/admins unchanged.
  // (isFleet is admin-only; manageableOrgIds is empty for a driver-only user.)
  const personalScope = !isFleet && manageableOrgIds.length === 0;
  const effectiveOrgId = personalScope ? null : dashboardOrgId;
  const enabled = personalScope || scopeReady;

  // Fleet-sized endpoint: WS session events invalidate it (useWsEvents); the
  // timer is only the fallback for what the WS does not publish (online/state
  // changes) — 60 s with a live WS, 30 s without, never in a hidden tab.
  const pollInterval = useFleetPollInterval();
  const hasDataRef = useRef(false);

  const query = useQuery<UnitsResponse>({
    queryKey: ["units", personalScope ? "personal" : dashboardOrgId, params.search, params.state, params.sort, params.start, params.end, params.mode ?? "", params.pricing ?? "", params.protocol ?? ""],
    queryFn: () =>
      fetchUnits({
        start: params.start,
        end: params.end,
        search: params.search || undefined,
        state: params.state || undefined,
        sort: params.sort || undefined,
        mode: params.mode || undefined,
        pricing: params.pricing || undefined,
        protocol: params.protocol || undefined,
        orgId: effectiveOrgId,
        refreshSource: refreshSource(hasDataRef.current),
      }),
    enabled,
    refetchInterval: pollInterval,
    refetchIntervalInBackground: false,
  });
  hasDataRef.current = query.data !== undefined;

  const { refetch: queryRefetch } = query;
  const refetch = useCallback(() => {
    markRefreshSource("user");
    return queryRefetch();
  }, [queryRefetch]);

  return {
    units: query.data?.units ?? [],
    total: query.data?.total ?? 0,
    noAccess: query.data?.no_units_assigned ?? false,
    degraded: query.data?.degraded ?? false,
    truncated: query.data?.truncated ?? false,
    pageLimit: query.data?.page_limit,
    isLoading: query.isLoading,
    error: query.error,
    refetch,
  };
}
